/**
 * Watered down version of https://github.com/habx/apollo-multi-endpoint-link
 *
 * MIT License
 *
 * Copyright (c) 2020 HABX
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in all
 * copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
 * SOFTWARE.
 */

import {
  ApolloLink,
  type FetchResult,
  type NextLink,
  type Operation,
  type RequestHandler,
  createHttpLink,
} from '@apollo/client/core'
import { hasDirectives, type Observable, removeDirectivesFromDocument } from '@apollo/client/utilities'
import { createPersistedQueryLink } from '@apollo/client/link/persisted-queries'
import { type OperationDefinitionNode, type StringValueNode } from 'graphql'
import { sha256 } from 'crypto-hash'
import zionDebug from '@fs/zion-debug'
import i18n from 'i18next'

const debug = zionDebug('ApolloMultiLink')

const getDirectiveArgumentValueFromOperation = (
  operation: Operation,
  directiveName: string,
  argumentName: string
): string =>
  (
    (
      operation.query.definitions.find(
        (definition) => definition.kind === 'OperationDefinition'
      ) as OperationDefinitionNode
    )?.directives
      ?.find((directive) => directive.name?.value === directiveName)
      ?.arguments?.find((argument) => argument.name?.value === argumentName)?.value as StringValueNode
  )?.value

type ApolloMultiLinkConfig = {
  /**
   * Base URL where the backend is found.
   */
  baseUrl: string

  /**
   * Dictionary of your endpoints.
   * Keys will be used as name identifier in the `@api` directive
   */
  endpoints: Record<string, string>

  /**
   * Sites that support subsites (e.g. ['fhsites'] would support 'fhsites/train', 'fhsites/train/other', etc.)
   */
  subsites: string[]

  /**
   *
   * @param endpoint
   * @param getCurrentContext
   * Callback function to set context like headers according to your endpoint.
   */
  // eslint-disable-next-line no-unused-vars
  getContext?: (endpoint: string, getCurrentContext: () => Record<string, unknown>) => Record<string, unknown>

  /**
   * Use the ECT proxy to cache GET requests
   */
  useEctProxy?: boolean
}

export default class ApolloMultiLink extends ApolloLink {
  private httpLink: ApolloLink

  private config: ApolloMultiLinkConfig

  private persistedQueryLink: ApolloLink

  constructor(config: ApolloMultiLinkConfig, request?: RequestHandler) {
    super(request)
    this.config = config
    if (config.useEctProxy) {
      this.httpLink = createHttpLink({ fetch: this.cacheableFetch })
    } else {
      this.httpLink = createHttpLink({ fetch: this.i18nFetch })
    }
    this.persistedQueryLink = createPersistedQueryLink({ sha256, useGETForHashedQueries: true })
  }

  private i18nFetch = async (url: string, options: RequestInit) => {
    // Copy the fslanguage header into the accept-language header
    if (!options.headers) {
      // eslint-disable-next-line no-console
      console.warn(`ApolloMultiLink.i18nFetch invoked with no headers, url=${url}`)
      options.headers = {}
    }
    options.headers['accept-language'] = i18n.language
    return fetch(url, options)
  }

  private cacheableFetch = async (url: string, options: RequestInit) => {
    if (url.includes('/graphql/delivery/custom-type-graphql') && options.method === 'GET') {
      // url contains the persistedQuery hash in the long, ugly URL
      // url=/service/zcms/<site>/graphql/delivery/custom-type-graphql?
      debug('cacheable custom-type-graphql detected')
      if (!options.headers) {
        // eslint-disable-next-line no-console
        console.warn(`ApolloMultiLink.cacheableFetch invoked with no headers, url=${url}`)
        options.headers = {}
      }
      options.headers['x-orig-url'] = url
      options.headers['accept-language'] = i18n.language

      // We rely on the NodeJS proxy that is part of the application to send the /zcms request to the correct endpoint
      const newUrl = `/zcms/webservice/ect-cache?orig_url=${encodeURIComponent(url)}`
      debug(`cacheableFetch fetch newUrl=${newUrl}`)
      return fetch(newUrl, options)
    }
    return fetch(url, options)
  }

  public request(
    operation: Operation,
    forward?: NextLink
  ): Observable<FetchResult<Record<string, unknown>, Record<string, unknown>, Record<string, unknown>>> | null {
    if (!hasDirectives(['api'], operation.query)) {
      return forward?.(operation) ?? null
    }

    let apiName: string = getDirectiveArgumentValueFromOperation(operation, 'api', 'name')

    if (!apiName) {
      const contextKey = getDirectiveArgumentValueFromOperation(operation, 'api', 'contextKey')

      if (contextKey) {
        apiName = operation.getContext()[contextKey]
      }
    }

    const query = removeDirectivesFromDocument([{ name: 'api', remove: true }], operation.query)

    if (!query) {
      throw new Error('Error while removing directive api')
    }

    operation.query = query

    if (this.config.getContext) {
      operation.setContext(this.config.getContext(apiName, operation.getContext))
    } else if (process.env.NODE_ENV === 'development') {
      throw new Error(`getContext() is not defined in configuration`)
    }

    // Check to see if this is a potential subsite. If so the apiName is the base of the URL
    const apiNameParts = apiName.split('/')
    if (
      apiNameParts.length > 1 &&
      this.config.subsites &&
      this.config.subsites.find((s) => s.startsWith(apiNameParts[0]))
    ) {
      const suffixMap = {
        'help-ctr': 'help-center',
        'loc-evt-cal': 'location-event-calendar',
        marketing: 'marketing',
        custom: 'custom-type-graphql',
        'zion-cms': 'zion-cms',
      }
      // apiName=centers/orange_county_california:help-ctr
      const [apiPathName, apiNameSuffix] = apiName.split(':')
      operation.setContext({
        uri: `${this.config.baseUrl}${apiPathName}/graphql/delivery/${suffixMap[apiNameSuffix || 'zion-cms']}`,
      })
    } else if (this.config.endpoints[apiName]) {
      operation.setContext({
        // uri='/service/zcms/centers/graphql/delivery/help-center'
        uri: this.config.endpoints[apiName],
      })
    } else {
      throw new Error(`${apiName} is not defined in endpoints or subsites definitions`)
    }

    const response = this.persistedQueryLink.concat(this.httpLink).request(operation, forward)

    return response
  }
}
